/**
* MIT License
*
* Copyright (c) Sindre Sorhus <sindresorhus@gmail.com> (https://sindresorhus.com)
*
* Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
*
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*
* THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*
**/

// because i cant use the original package on my code, because its full ESM.
// i fork the modify the code and respect the license.

import { Buffer } from 'buffer'

type ErrorConstructor = new (message?: string) => Error

const errorConstructors = new Map<string, ErrorConstructor>([
  // Native ES errors
  EvalError,
  RangeError,
  ReferenceError,
  SyntaxError,
  TypeError,
  URIError,

  // Built-in errors
  globalThis.DOMException,

  // Node-specific errors
  globalThis.AssertionError,
  globalThis.SystemError,
]
  .filter(Boolean)
  .map((constructor: ErrorConstructor) => [constructor.name, constructor]),
)
export class NonError extends Error {
  name = `NonError`

  constructor(message: any) {
    super(NonError._prepareSuperMessage(message))
  }

  private static _prepareSuperMessage(message: any): string {
    try {
      return JSON.stringify(message)
    } catch {
      return String(message)
    }
  }
}

interface CommonProperty {
  property: string
  enumerable: boolean
}
const commonProperties: CommonProperty[] = [
  { property: `name`, enumerable: false },
  { property: `message`, enumerable: false },
  { property: `stack`, enumerable: false },
  { property: `code`, enumerable: true },
  { property: `cause`, enumerable: false },
]

const toJsonWasCalled = Symbol(`.toJSON was called`)

const toJSON = (from: any): any => {
  from[toJsonWasCalled] = true
  const json = from.toJSON()
  delete from[toJsonWasCalled]
  return json
}

const getErrorConstructor = (name: string): ErrorConstructor => errorConstructors.get(name) ?? Error

interface DestroyCircularOptions {
  from: any
  seen: Set<any>
  to_?: any
  forceEnumerable?: boolean
  maxDepth: number
  depth: number
  useToJSON?: boolean
}
const destroyCircular = ({
  from,
  seen,
  to_,
  forceEnumerable,
  maxDepth,
  depth,
  useToJSON,
}: DestroyCircularOptions): any => {
  const to = to_ ?? (Array.isArray(from) ? [] : {})

  seen.add(from)

  if (depth >= maxDepth) return to
  if (useToJSON && typeof from.toJSON === `function` && !(toJsonWasCalled in from))
    return toJSON(from)

  const destroyLocal = (value: any): any => {
    const ErrorConstructor = getErrorConstructor(value.name)
    return destroyCircular({
      from: value,
      seen: new Set(seen),
      to_: isErrorLike(value) ? new ErrorConstructor() : undefined,
      forceEnumerable,
      maxDepth,
      depth,
      useToJSON,
    })
  }

  for (const [key, value] of Object.entries(from)) {
    if (typeof Buffer === `function` && Buffer.isBuffer(value)) {
      to[key] = `[object Buffer]`
      continue
    }

    if (value !== null && typeof value === `object` && typeof value[`pipe`] === `function`) {
      to[key] = `[object Stream]`
      continue
    }

    if (typeof value === `function`) continue
    if (!value || typeof value !== `object`) {
      to[key] = value
      continue
    }

    if (!seen.has(value)) {
      depth++
      to[key] = destroyLocal(value)
      continue
    }

    to[key] = `[Circular]`
  }

  for (const { property, enumerable } of commonProperties) {
    if (typeof from[property] !== `undefined` && from[property] !== null) {
      Object.defineProperty(to, property, {
        value: isErrorLike(from[property]) ? destroyLocal(from[property]) : from[property],
        enumerable: forceEnumerable ? true : enumerable,
        configurable: true,
        writable: true,
      })
    }
  }

  return to
}

interface SerializeErrorOptions {
  maxDepth?: number
  useToJSON?: boolean
}

export function serializeError(value: any, options: SerializeErrorOptions = {}): any {
  const {
    maxDepth = Number.POSITIVE_INFINITY,
    useToJSON = true,
  } = options

  if (typeof value === `object` && value !== null) {
    return destroyCircular({
      from: value,
      seen: new Set(),
      forceEnumerable: true,
      maxDepth,
      depth: 0,
      useToJSON,
    })
  }

  if (typeof value === `function`)
    return `[Function: ${ value.name || `anonymous` }]`

  return value
}

interface DeserializeErrorOptions {
  maxDepth?: number
}
export function deserializeError(value: any, options: DeserializeErrorOptions = {}): Error {
  const { maxDepth = Number.POSITIVE_INFINITY } = options

  if (value instanceof Error) return value
  if (typeof value === `object` && value !== null && !Array.isArray(value)) {
    const ErrorConstructor = getErrorConstructor(value.name)
    return destroyCircular({
      from: value,
      seen: new Set(),
      to_: new ErrorConstructor(),
      maxDepth,
      depth: 0,
    })
  }

  return new NonError(value)
}

export function isErrorLike(value: any): boolean {
  return (
    value &&
    typeof value === `object` &&
    `name` in value &&
    `message` in value &&
    `stack` in value
  )
}
